import { useState, type FormEvent } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MarqueObligatoire } from '../ui/Primitives';
import { aujourdhui } from '../../lib/dates-civiles';
import { euros, messageRefus } from '../../lib/useFormateurs';
import type { SaisiePaiement } from '../../lib/useCircuitFactures';

/**
 * Fenêtres des gestes du circuit des factures formateurs (TF-401) : un motif
 * (renvoi, refus, annulation d'un paiement) ou un paiement (jour du virement,
 * référence). L'API juge ; la fenêtre dit son refus tel quel.
 */

export const CLASSE_CHAMP_CIRCUIT =
  'min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

export const MOTIF_MAX = 500;

export function ModaleMotif({
  titre,
  sousTitre,
  libelle,
  aide,
  bouton,
  enCours,
  erreur,
  onConfirmer,
  onFermer,
}: {
  titre: string;
  sousTitre: string;
  libelle: string;
  aide: string;
  bouton: string;
  enCours: boolean;
  erreur: unknown;
  onConfirmer: (motif: string) => void;
  onFermer: () => void;
}): JSX.Element {
  const [motif, setMotif] = useState('');
  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    onConfirmer(motif.trim());
  };

  return (
    <Modale titre={titre} sousTitre={sousTitre} ouverte onFermer={onFermer} zIndice="z-[60]">
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            {libelle}
            <MarqueObligatoire />
          </span>
          <textarea
            required
            rows={4}
            maxLength={MOTIF_MAX}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            className={`${CLASSE_CHAMP_CIRCUIT} resize-y`}
          />
        </label>
        <p className="text-xs text-codialis-gray">{aide}</p>
        {erreur !== null && erreur !== undefined && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageRefus(erreur, 'Le geste n’a pas pu être enregistré.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={enCours || motif.trim() === ''}>
            {enCours ? 'Enregistrement…' : bouton}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

export function ModalePaiement({
  titre,
  resume,
  transmisLe,
  enCours,
  erreur,
  onConfirmer,
  onFermer,
}: {
  titre: string;
  /** « 3 factures · 1 995,00 € » ou le formateur et le montant. */
  resume: string;
  /** Jour (`AAAA-MM-JJ`) de la transmission : le virement ne la précède pas. */
  transmisLe: string | null;
  enCours: boolean;
  erreur: unknown;
  onConfirmer: (saisie: SaisiePaiement) => void;
  onFermer: () => void;
}): JSX.Element {
  const jour = aujourdhui();
  const [saisie, setSaisie] = useState<SaisiePaiement>({ payeeLe: jour, reference: '' });
  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    onConfirmer(saisie);
  };

  return (
    <Modale titre={titre} sousTitre={resume} ouverte onFermer={onFermer} zIndice="z-[60]" largeur="max-w-lg">
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Jour du virement
            <MarqueObligatoire />
          </span>
          <input
            type="date"
            required
            value={saisie.payeeLe}
            min={transmisLe ?? undefined}
            max={jour}
            onChange={(evenement) => setSaisie({ ...saisie, payeeLe: evenement.target.value })}
            className={CLASSE_CHAMP_CIRCUIT}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Référence du virement (facultatif)
          </span>
          <input
            type="text"
            maxLength={100}
            value={saisie.reference}
            onChange={(evenement) => setSaisie({ ...saisie, reference: evenement.target.value })}
            className={CLASSE_CHAMP_CIRCUIT}
          />
        </label>
        <p className="text-xs text-codialis-gray">
          Le formateur est prévenu du paiement, dans son espace et par e-mail. Un paiement noté par erreur s’annule avec
          un motif.
        </p>
        {erreur !== null && erreur !== undefined && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageRefus(erreur, 'Le paiement n’a pas pu être noté.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={enCours || saisie.payeeLe === ''}>
            {enCours ? 'Enregistrement…' : 'Noter le paiement'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

/** « 3 factures · 1 995,00 € ». */
export function resumeMontant(nombre: number, montant: number): string {
  return `${nombre} facture${nombre > 1 ? 's' : ''} · ${euros(montant)}`;
}
